import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductShowcase from './ProductShowcase';
import ProductDocsSection from './ProductDocsSection';
import {
  findSeriesById,
  getChildSeries,
  getRootSeries,
  getSelfAndDescendantIds,
  getSeriesPathCn,
  seriesHasProducts,
} from '../utils/seriesUtils';
import { isRealSite, withRealPrefix } from '../utils/siteMode';

const API_BASE_URL = 'https://www.lankastudio.cn/api';
const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function stripBracketDecor(name) {
  let s = String(name || '').trim();
  if (
    (s.startsWith('【') && s.endsWith('】')) ||
    (s.startsWith('[') && s.endsWith(']'))
  ) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

function Products() {
  const navigate = useNavigate();
  const location = useLocation();
  const isReal = isRealSite(location.pathname);
  const isMobile = useIsMobile();
  const [allProducts, setAllProducts] = useState([]);
  const [series, setSeries] = useState([]);
  /** null = 全部；'none' = 无系列；number = 系列 id */
  const [selectedRootId, setSelectedRootId] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    fetchSeries();
    fetchProducts();
  }, []);

  const fetchSeries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/series/`);
      if (!response.ok) return;
      const data = await response.json();
      setSeries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('获取系列失败:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/products/`);
      if (!response.ok) throw new Error('获取产品失败');
      const data = await response.json();
      setAllProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const rootSeries = useMemo(() => {
    return getRootSeries(series).filter((s) => seriesHasProducts(s.id, series, allProducts));
  }, [series, allProducts]);

  const childSeries = useMemo(() => {
    if (selectedRootId == null || selectedRootId === 'none') return [];
    return getChildSeries(selectedRootId, series).filter((s) =>
      seriesHasProducts(s.id, series, allProducts)
    );
  }, [selectedRootId, series, allProducts]);

  const hasUncategorized = useMemo(
    () => allProducts.some((p) => p.series_id == null || Number(p.series_id) <= 0),
    [allProducts]
  );

  const products = useMemo(() => {
    let list = [...allProducts];

    if (selectedRootId === 'none') {
      list = list.filter((p) => p.series_id == null || Number(p.series_id) <= 0);
    } else if (selectedRootId != null) {
      const focusId = selectedChildId != null ? selectedChildId : selectedRootId;
      const ids = new Set(getSelfAndDescendantIds(focusId, series));
      list = list.filter((p) => p.series_id != null && ids.has(Number(p.series_id)));
    }

    const seriesMap = new Map(series.map((s) => [Number(s.id), s]));
    list.sort((a, b) => {
      const sa = a.series_id != null ? seriesMap.get(Number(a.series_id)) : null;
      const sb = b.series_id != null ? seriesMap.get(Number(b.series_id)) : null;
      const pathA = sa ? getSeriesPathCn(sa, series) : '\uffff';
      const pathB = sb ? getSeriesPathCn(sb, series) : '\uffff';
      const byPath = pathA.localeCompare(pathB, 'zh-CN');
      if (byPath !== 0) return byPath;
      return (a.product_name_cn || '').localeCompare(b.product_name_cn || '', 'zh-CN');
    });
    return list;
  }, [allProducts, series, selectedRootId, selectedChildId]);

  const selectRoot = (id) => {
    setSelectedRootId(id);
    setSelectedChildId(null);
  };

  const openProductDetail = async (product) => {
    if (isMobile) {
      navigate(isReal ? withRealPrefix(`/products/${product.id}`) : `/products/${product.id}`);
      return;
    }
    setSelectedProduct(product);
    try {
      const res = await fetch(`${API_BASE_URL}/products/${product.id}`);
      if (res.ok) {
        const full = await res.json();
        setSelectedProduct(full);
      }
    } catch (err) {
      console.error('获取产品详情失败:', err);
    }
  };

  const closeProductDetail = () => setSelectedProduct(null);

  const seriesLabel = (s) => stripBracketDecor(s.series_name_cn || s.series_name_en || `系列#${s.id}`);

  return (
    <div style={styles.container}>
      <ProductShowcase />
      <div style={styles.overlay} />

      <div style={isMobile ? { ...styles.contentLayer, padding: '16px 12px' } : styles.contentLayer}>
        <div style={styles.topBar}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={isMobile ? { ...styles.backButton, padding: '8px 14px', fontSize: 13 } : styles.backButton}
          >
            ← 返回
          </button>
          {!loading && !error ? (
            <span style={styles.resultCount}>{products.length} 件产品</span>
          ) : null}
        </div>

        <header style={isMobile ? { ...styles.header, marginBottom: 16 } : styles.header}>
          <h1 style={isMobile ? { ...styles.title, fontSize: 28, marginBottom: 6 } : styles.title}>
            工作室产品
          </h1>
          <p style={isMobile ? { ...styles.subtitle, fontSize: 13 } : styles.subtitle}>
            按系列浏览模玩作品
          </p>
        </header>

        {/* 一级：根系列 */}
        <div
          style={
            isMobile
              ? {
                  ...styles.seriesBar,
                  gap: 8,
                  marginBottom: childSeries.length ? 10 : 20,
                  justifyContent: 'flex-start',
                  overflowX: 'auto',
                  flexWrap: 'nowrap',
                  paddingBottom: 4,
                  WebkitOverflowScrolling: 'touch',
                }
              : { ...styles.seriesBar, marginBottom: childSeries.length ? 12 : 28 }
          }
        >
          <FilterChip
            active={selectedRootId == null}
            onClick={() => selectRoot(null)}
            isMobile={isMobile}
            label="全部"
          />
          {rootSeries.map((s) => (
            <FilterChip
              key={s.id}
              active={Number(selectedRootId) === Number(s.id)}
              onClick={() => selectRoot(s.id)}
              isMobile={isMobile}
              label={seriesLabel(s)}
            />
          ))}
          {hasUncategorized && (
            <FilterChip
              active={selectedRootId === 'none'}
              onClick={() => selectRoot('none')}
              isMobile={isMobile}
              label="其他"
            />
          )}
        </div>

        {/* 二级：子系列（仅在选中根系列且有子项时出现） */}
        {childSeries.length > 0 && (
          <div
            style={
              isMobile
                ? {
                    ...styles.subSeriesBar,
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                    WebkitOverflowScrolling: 'touch',
                  }
                : styles.subSeriesBar
            }
          >
            <FilterChip
              active={selectedChildId == null}
              onClick={() => setSelectedChildId(null)}
              isMobile={isMobile}
              label="全部子系列"
              subtle
            />
            {childSeries.map((s) => (
              <FilterChip
                key={s.id}
                active={Number(selectedChildId) === Number(s.id)}
                onClick={() => setSelectedChildId(s.id)}
                isMobile={isMobile}
                label={seriesLabel(s)}
                subtle
              />
            ))}
          </div>
        )}

        {loading ? (
          <div style={isMobile ? { ...styles.loading, padding: 40 } : styles.loading}>
            <div style={isMobile ? { ...styles.spinner, width: 32, height: 32 } : styles.spinner} />
            <p style={isMobile ? { fontSize: 14 } : undefined}>加载中...</p>
          </div>
        ) : error ? (
          <div style={isMobile ? { ...styles.error, padding: 40 } : styles.error}>
            <p style={isMobile ? { fontSize: 14 } : undefined}>❌ {error}</p>
            <button type="button" onClick={fetchProducts} style={styles.retryButton}>
              重试
            </button>
          </div>
        ) : products.length === 0 ? (
          <div style={isMobile ? { ...styles.emptyState, padding: '40px 16px' } : styles.emptyState}>
            <span style={isMobile ? { ...styles.emptyIcon, fontSize: 36 } : styles.emptyIcon}>📦</span>
            <p style={isMobile ? { ...styles.emptyText, fontSize: 14 } : styles.emptyText}>
              该系列暂无产品
            </p>
          </div>
        ) : (
          <div
            style={
              isMobile
                ? { ...styles.productsGrid, gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }
                : styles.productsGrid
            }
          >
            {products.map((product) => {
              const ser = findSeriesById(product.series_id, series);
              const pathCn = ser ? getSeriesPathCn(ser, series) : '';
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  seriesPath={pathCn}
                  onClick={() => openProductDetail(product)}
                  isMobile={isMobile}
                />
              );
            })}
          </div>
        )}

        {selectedProduct && (
          <ProductModal
            product={selectedProduct}
            seriesList={series}
            onClose={closeProductDetail}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, isMobile, subtle = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.seriesTag,
        ...(subtle ? styles.subSeriesTag : null),
        ...(isMobile && { padding: subtle ? '5px 12px' : '6px 14px', fontSize: 13, flexShrink: 0 }),
        backgroundColor: active ? (subtle ? 'rgba(18, 183, 245, 0.25)' : '#12B7F5') : 'transparent',
        borderColor: active ? '#12B7F5' : subtle ? 'rgba(61, 68, 80, 0.8)' : '#3d4450',
        color: active ? '#fff' : subtle ? '#9ca3af' : '#c4c9d2',
      }}
    >
      {label}
    </button>
  );
}

function productCardImageUrl(product) {
  if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    if (first?.url) return first.url;
    if (first?.id != null) return `https://www.lankastudio.cn/api/images/${first.id}`;
  }
  if (product?.cover_image_url) return product.cover_image_url;
  if (product?.cover_image_id) return `https://www.lankastudio.cn/api/images/${product.cover_image_id}`;
  return null;
}

function ProductCard({ product, seriesPath, onClick, isMobile }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageUrl = productCardImageUrl(product);
  const desc = (product.description_cn || product.product_description_cn || '').trim();

  const cardStyle = isMobile ? { ...styles.productCard, borderRadius: 12 } : styles.productCard;
  const imgContainerStyle = isMobile
    ? { ...styles.imageContainer, height: 140 }
    : styles.imageContainer;
  const infoStyle = isMobile ? { ...styles.productInfo, padding: 12 } : styles.productInfo;
  const nameStyle = isMobile
    ? { ...styles.productName, fontSize: 14, marginBottom: 2 }
    : styles.productName;
  const nameEnStyle = isMobile
    ? { ...styles.productNameEn, fontSize: 11, marginBottom: 4 }
    : styles.productNameEn;
  const pathStyle = isMobile
    ? { ...styles.seriesPath, fontSize: 10, marginBottom: 4 }
    : styles.seriesPath;
  const priceStyle = isMobile
    ? { ...styles.priceTag, top: 6, right: 6, padding: '4px 8px', fontSize: 12 }
    : styles.priceTag;

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!isMobile) {
          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.4)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
      }}
    >
      <div style={imgContainerStyle}>
        {imageUrl ? (
          <>
            {!imageLoaded && <div style={styles.imagePlaceholder}>📷</div>}
            <img
              src={imageUrl}
              alt={product.product_name_cn}
              loading="lazy"
              decoding="async"
              style={{
                ...styles.productImage,
                opacity: imageLoaded ? 1 : 0,
              }}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div style={styles.imagePlaceholder}>📷</div>
        )}

        {product.price ? <div style={priceStyle}>¥{product.price}</div> : null}
      </div>

      <div style={infoStyle}>
        {seriesPath ? <div style={pathStyle}>{seriesPath}</div> : null}
        <h3 style={nameStyle}>{product.product_name_cn}</h3>
        {product.product_name_en ? <p style={nameEnStyle}>{product.product_name_en}</p> : null}
        {desc ? (
          <p style={isMobile ? { ...styles.productDesc, fontSize: 11, marginBottom: 8 } : styles.productDesc}>
            {desc.slice(0, isMobile ? 36 : 72)}
            {desc.length > (isMobile ? 36 : 72) ? '…' : ''}
          </p>
        ) : null}

        {(product.category || product.status) && (
          <div style={isMobile ? { ...styles.tags, gap: 4 } : styles.tags}>
            {product.category ? (
              <span style={isMobile ? { ...styles.tag, fontSize: 10, padding: '2px 6px' } : styles.tag}>
                {product.category}
              </span>
            ) : null}
            {product.status ? (
              <span
                style={{
                  ...styles.tag,
                  ...(isMobile && { fontSize: 10, padding: '2px 6px' }),
                  backgroundColor:
                    product.status === '在售' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 191, 36, 0.2)',
                  color: product.status === '在售' ? '#22c55e' : '#fbbf24',
                }}
              >
                {product.status}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (img.url) return img.url;
  if (img.id) return `${API_BASE_URL}/images/${img.id}`;
  return null;
}

function ProductModal({ product, seriesList = [], onClose, isMobile }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = product.images || [];
  const rawSpecs = product.specifications || [];
  const specs = [...rawSpecs].sort((a, b) => {
    const getScale = (s) => {
      const m = String(s.model_scale || '').match(/:(\d+)|(\d+)$|\/(\d+)/);
      return parseInt(m ? m[1] || m[2] || m[3] : '0', 10) || 999;
    };
    return getScale(b) - getScale(a);
  });
  const purchaseLinks = product.purchase_links || [];
  const descCn = (product.description_cn || product.product_description_cn || '').trim();
  const descEn = (product.description_en || product.product_description_en || '').trim();
  const ser = findSeriesById(product.series_id, seriesList);
  const pathCn = ser ? getSeriesPathCn(ser, seriesList) : '';

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () =>
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const modalStyle = isMobile ? { ...styles.modal, maxHeight: '95vh', borderRadius: 16 } : styles.modal;
  const modalOverlayStyle = isMobile ? { ...styles.modalOverlay, padding: 12 } : styles.modalOverlay;
  const imageSectionStyle = isMobile
    ? { ...styles.modalImageSection, height: 220 }
    : styles.modalImageSection;
  const modalInfoStyle = isMobile ? { ...styles.modalInfo, padding: 16 } : styles.modalInfo;
  const modalTitleStyle = isMobile
    ? { ...styles.modalTitle, fontSize: 20, marginBottom: 4 }
    : styles.modalTitle;
  const modalSubtitleStyle = isMobile
    ? { ...styles.modalSubtitle, fontSize: 13, marginBottom: 8 }
    : styles.modalSubtitle;
  const modalDescStyle = isMobile
    ? { ...styles.modalDesc, fontSize: 14, marginBottom: 8 }
    : styles.modalDesc;
  const modalDescEnStyle = isMobile
    ? { ...styles.modalDescEn, fontSize: 12, marginBottom: 12 }
    : styles.modalDescEn;
  const modalTagsStyle = isMobile
    ? { ...styles.modalTags, gap: 6, marginBottom: 16 }
    : styles.modalTags;
  const modalTagStyle = isMobile
    ? { ...styles.modalTag, padding: '4px 10px', fontSize: 12 }
    : styles.modalTag;
  const purchaseBtnStyle = isMobile
    ? { ...styles.purchaseButton, padding: '12px 24px', fontSize: 14 }
    : styles.purchaseButton;
  const closeBtnStyle = isMobile
    ? {
        ...styles.modalClose,
        top: 'max(12px, env(safe-area-inset-top, 12px))',
        right: 12,
        width: 44,
        height: 44,
        fontSize: 20,
        zIndex: 20,
      }
    : styles.modalClose;
  const carouselBtnStyle = isMobile
    ? { ...styles.carouselBtn, width: 36, height: 36, fontSize: 20 }
    : styles.carouselBtn;

  const currentImageUrl = getImageUrl(images[currentImageIndex]);

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} style={closeBtnStyle}>
          ✕
        </button>

        <div style={imageSectionStyle}>
          {images.length > 0 && currentImageUrl ? (
            <>
              <img src={currentImageUrl} alt={product.product_name_cn} style={styles.modalImage} />
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    style={{ ...carouselBtnStyle, left: isMobile ? 6 : 10 }}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    style={{ ...carouselBtnStyle, right: isMobile ? 6 : 10 }}
                  >
                    ›
                  </button>
                  <div style={styles.imageIndicators}>
                    {images.map((_, idx) => (
                      <span
                        key={idx}
                        style={{
                          ...styles.indicator,
                          backgroundColor:
                            idx === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                        }}
                        onClick={() => setCurrentImageIndex(idx)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div style={styles.noImage}>暂无图片</div>
          )}
        </div>

        <div style={modalInfoStyle}>
          {pathCn ? <div style={styles.modalSeriesPath}>{pathCn}</div> : null}
          <h2 style={modalTitleStyle}>{product.product_name_cn}</h2>
          {product.product_name_en ? <p style={modalSubtitleStyle}>{product.product_name_en}</p> : null}

          <div style={styles.specsSection}>
            <h4 style={styles.specsTitle}>规格与售价</h4>
            {specs.length > 0 ? (
              <div style={styles.specsTableWrap}>
                <table style={styles.specsTable}>
                  <thead>
                    <tr>
                      <th style={styles.specsTh}>比例</th>
                      <th style={styles.specsTh}>尺寸(cm)</th>
                      <th style={styles.specsTh}>白模</th>
                      <th style={styles.specsTh}>涂装</th>
                      {specs.some((s) => s.remark) && <th style={styles.specsTh}>备注</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {specs.map((s) => (
                      <tr key={s.id}>
                        <td style={styles.specsTd}>{s.model_scale}</td>
                        <td style={styles.specsTd}>
                          {s.size_x}×{s.size_y}×{s.size_z}
                        </td>
                        <td style={styles.specsTd}>
                          {s.white_model_price != null
                            ? `¥${Number(s.white_model_price).toFixed(2)}`
                            : '-'}
                        </td>
                        <td style={styles.specsTd}>
                          {s.painted_price != null ? `¥${Number(s.painted_price).toFixed(2)}` : '-'}
                        </td>
                        {specs.some((sp) => sp.remark) && (
                          <td style={styles.specsTd}>{s.remark || '-'}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : product.price ? (
              <p style={styles.specsEmpty}>¥{product.price}</p>
            ) : (
              <p style={styles.specsEmpty}>规格与售价信息待补充</p>
            )}
          </div>

          {descCn ? <p style={modalDescStyle}>{descCn}</p> : null}
          {descEn ? <p style={modalDescEnStyle}>{descEn}</p> : null}

          <ProductDocsSection product={product} compact />

          {(product.category || product.status) && (
            <div style={modalTagsStyle}>
              {product.category ? <span style={modalTagStyle}>{product.category}</span> : null}
              {product.status ? <span style={modalTagStyle}>{product.status}</span> : null}
            </div>
          )}

          <div style={styles.purchaseLinksWrap}>
            <h4 style={styles.purchaseLinksTitle}>购买渠道</h4>
            {purchaseLinks.length > 0 ? (
              <div style={styles.purchaseLinksGrid}>
                {purchaseLinks.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={purchaseBtnStyle}
                  >
                    {link.icon} {link.channel}
                  </a>
                ))}
              </div>
            ) : product.purchase_link ? (
              <a
                href={product.purchase_link}
                target="_blank"
                rel="noopener noreferrer"
                style={purchaseBtnStyle}
              >
                🛒 立即购买
              </a>
            ) : (
              <div>
                <p style={styles.purchaseLinksEmpty}>暂无上架信息，敬请期待。</p>
                <a
                  href="https://b23.tv/c9gCtts"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.shopLink}
                >
                  📺 前往 B 站店铺
                </a>
              </div>
            )}
          </div>

          {isMobile && (
            <div style={styles.modalMobileFooter}>
              <button type="button" onClick={onClose} style={styles.modalCloseBottom}>
                关闭
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: '#0f1115',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 17, 21, 0.75)',
    zIndex: 1,
  },
  contentLayer: {
    position: 'relative',
    zIndex: 2,
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  resultCount: {
    fontSize: 13,
    color: '#9ca3af',
    whiteSpace: 'nowrap',
  },
  header: {
    textAlign: 'center',
    marginBottom: 28,
  },
  title: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: '8px',
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  },
  subtitle: {
    fontSize: '16px',
    color: '#9ca3af',
  },
  seriesBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: 28,
    justifyContent: 'center',
  },
  subSeriesBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 28,
    justifyContent: 'center',
    padding: '10px 12px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  seriesTag: {
    padding: '9px 18px',
    borderRadius: '999px',
    border: '1px solid',
    backgroundColor: 'transparent',
    color: '#c4c9d2',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  subSeriesTag: {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '16px',
  },
  loading: {
    textAlign: 'center',
    padding: '60px',
    color: '#9ca3af',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(255, 255, 255, 0.1)',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  error: {
    textAlign: 'center',
    padding: '60px',
    color: '#ef4444',
  },
  retryButton: {
    marginTop: '20px',
    padding: '10px 24px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '24px',
  },
  productCard: {
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
  },
  imageContainer: {
    position: 'relative',
    height: '220px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease',
  },
  imagePlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'rgba(255, 255, 255, 0.2)',
  },
  priceTag: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '700',
  },
  productInfo: {
    padding: '18px',
  },
  seriesPath: {
    fontSize: 12,
    color: '#12B7F5',
    fontWeight: 600,
    marginBottom: 6,
    letterSpacing: 0.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '4px',
  },
  productNameEn: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '8px',
  },
  productDesc: {
    fontSize: '13px',
    color: '#9ca3af',
    lineHeight: '1.5',
    marginBottom: '12px',
  },
  tags: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '12px',
    padding: '4px 10px',
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    color: '#60a5fa',
    borderRadius: '4px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    position: 'relative',
    backgroundColor: '#1f2937',
    borderRadius: '20px',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  modalMobileFooter: {
    padding: '16px',
    paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    flexShrink: 0,
  },
  modalCloseBottom: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    color: '#fff',
    fontSize: '20px',
    cursor: 'pointer',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalImageSection: {
    position: 'relative',
    height: '400px',
    backgroundColor: '#000',
  },
  modalImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  noImage: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    fontSize: '18px',
  },
  carouselBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    border: 'none',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
  },
  imageIndicators: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  indicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    cursor: 'pointer',
  },
  modalInfo: {
    padding: '30px',
  },
  modalSeriesPath: {
    fontSize: 13,
    color: '#12B7F5',
    fontWeight: 700,
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: '8px',
  },
  modalSubtitle: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '16px',
  },
  modalDesc: {
    fontSize: '16px',
    color: '#d1d5db',
    lineHeight: '1.8',
    marginBottom: '12px',
  },
  modalDescEn: {
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  modalTags: {
    display: 'flex',
    gap: '10px',
    marginBottom: '24px',
  },
  modalTag: {
    padding: '6px 14px',
    backgroundColor: 'rgba(59, 130, 246, 0.2)',
    color: '#60a5fa',
    borderRadius: '6px',
    fontSize: '14px',
  },
  purchaseButton: {
    display: 'inline-block',
    padding: '14px 32px',
    backgroundColor: '#22c55e',
    color: '#fff',
    borderRadius: '10px',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
  specsSection: {
    marginBottom: 20,
  },
  specsTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    margin: '0 0 10px 0',
  },
  specsEmpty: {
    fontSize: 14,
    color: '#6b7280',
    margin: 0,
    fontStyle: 'italic',
  },
  specsTableWrap: {
    overflowX: 'auto',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)',
  },
  specsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  specsTh: {
    padding: '8px 12px',
    textAlign: 'left',
    color: '#9ca3af',
    fontWeight: 600,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  specsTd: {
    padding: '8px 12px',
    color: '#d1d5db',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  purchaseLinksWrap: {
    marginTop: 16,
  },
  purchaseLinksTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#9ca3af',
    margin: '0 0 10px 0',
  },
  purchaseLinksEmpty: {
    fontSize: 14,
    color: '#6b7280',
    margin: '0 0 10px 0',
    fontStyle: 'italic',
  },
  shopLink: {
    fontSize: 14,
    color: '#60a5fa',
    textDecoration: 'none',
  },
  purchaseLinksGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Products;
